import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { addDays, teethLabel, type TeethAgeCode } from '@cattle/money'
import type {
  AccessLogResponse,
  AnimalDetail,
  AnimalSummary,
  AskedAboutResponse,
  AuditEntry,
  BackupListResponse,
  BusinessDetailResponse,
  BusinessSummary,
  CostBreakdownReportResponse,
  DashboardResponse,
  HealthSummaryResponse,
  MeResponse,
  PaymentMixResponse,
  Invoice as ContractInvoice,
  Person as ContractPerson,
  Plan as ContractPlan,
  PlatformOverviewResponse,
  PlatformSettingsResponse,
  RealityCheckResponse,
  StorageUsageResponse,
  SubscriptionsSummaryResponse,
  TeamMember as ContractTeamMember,
  TenantSettingsResponse,
  Ticket as ContractTicket,
  UsageSummaryResponse,
} from '@cattle/contracts'

import { useSessionState } from '../app/session'
import { ApiRequestError, get, getList, shouldRetry } from '../lib/apiClient'
import { formatDateShort, monthName } from '../lib/format'
import { t } from '../lib/i18n'
import { useAccessGrantFor, type AccessGrant } from '../lib/platformAccess'

import type {
  AccessLogSummary,
  ActivityEntry,
  BackupState,
  BookAnimal,
  BookSummary,
  Business,
  ConsoleSettings,
  HealthSummary,
  Invoice,
  OwnerOverview,
  OwnerSettings,
  PaymentMix,
  Person,
  Plan,
  PlatformOverview,
  SubscriptionsSummary,
  TeamMember,
  Ticket,
  UsageSummary,
} from './types'

/**
 * THE SEAM — now wired.
 *
 * Each domain still gets exactly one hook, and each hook still has exactly one
 * line that produces data; that line is now a fetch. Nothing below this file
 * knows where its data came from, and no screen was reshaped to accommodate a
 * payload: where the contract and the fixture's guess disagreed, the mapping
 * happens HERE, in the `from…` functions, because that is what makes the
 * disagreement a five-line diff rather than a ten-component one.
 *
 * Three rules run through the whole file.
 *
 * **Money may legitimately be absent.** Every `...Paisa` from the API is
 * optional — the server strips the key for a Staff actor and behind Price Lock
 * (§4.16) — so nothing here substitutes `0`. A zero is a claim about someone's
 * money; `undefined` is the truth, and `<Money>` renders it.
 *
 * **A ratio is optional for the same reason.** Share-of-revenue is revenue, so
 * `PLATFORM_SUPPORT` does not receive it. `formatPercent(undefined)` is "—".
 *
 * **A limit of `null` is unlimited, never zero.** `limitSchema` is nullable
 * throughout; `0` there would mean "none allowed", which is a different and
 * much worse statement.
 */

/* ── the four states ──────────────────────────────────────────────────────── */

/**
 * A demo escape hatch, kept alive on purpose. Every data screen owes the reader
 * four states, and three of them are hard to reach against a healthy API.
 * `?demo=loading`, `?demo=error` and `?demo=empty` make them reachable in dev
 * without unplugging anything.
 */
export type DemoState = 'ok' | 'loading' | 'error' | 'empty'

function demoState(): DemoState {
  if (typeof window === 'undefined') return 'ok'
  const value = new URLSearchParams(window.location.search).get('demo')
  return value === 'loading' || value === 'error' || value === 'empty' ? value : 'ok'
}

/** The error body a screen would really receive, `requestId` and all. */
function demoError(path: string): ApiRequestError {
  return new ApiRequestError(500, {
    code: 'INTERNAL',
    message: 'Something went wrong. Please try again.',
    requestId: `req_demo_${path.replace(/\W+/g, '_')}`,
  })
}

/**
 * The error type a screen sees. Both `ApiRequestError` and `ApiTransportError`
 * carry `code`, `message` and `requestId`, so `ErrorState` needs one shape and
 * `shouldRetry` can still tell a refusal from a dead connection.
 */
export type FixtureQuery<T> = UseQueryResult<T, ApiRequestError>

interface ApiQueryOptions<TData, TSelected> {
  /** `?demo=empty` returns this instead of calling the API. */
  empty?: () => TData
  /** A query that cannot run yet — an id still being resolved, say. */
  enabled?: boolean
  /**
   * Reshape the payload for a screen, WITHOUT giving the reshaped value a cache
   * entry of its own.
   *
   * That distinction is the whole reason this exists. A derivation wrapped in a
   * second `useQuery` gets its own key, and a key that does not change when the
   * source changes serves the old mapping forever: suspend a business and the
   * status badge kept reading ACTIVE, because the id it was keyed on had not
   * moved. `select` re-runs whenever the source data does, so a view derived
   * this way cannot disagree with what it was derived from.
   */
  select?: (data: TData) => TSelected
}

function useApi<TData, TSelected = TData>(
  key: readonly unknown[],
  fetcher: () => Promise<TData>,
  options: ApiQueryOptions<TData, TSelected> = {},
): FixtureQuery<TSelected> {
  const state = demoState()
  const { empty, enabled = true, select } = options

  return useQuery<TData, ApiRequestError, TSelected>({
    queryKey: [...key, state],
    retry: shouldRetry,
    staleTime: 30_000,
    queryFn: async () => {
      if (state === 'error') throw demoError(String(key[0]))
      if (state === 'empty' && empty) return empty()
      return fetcher()
    },
    ...(select ? { select } : {}),
    // `?demo=loading` never settles, which is exactly the state being shown.
    enabled: enabled && state !== 'loading',
  })
}

/* ── unit conversions, in one place ───────────────────────────────────────── */

const MB = 1024 * 1024
const GB = 1024 * MB

/** Bytes → MB. `formatBytes` speaks MB, and storage is quoted in whole units. */
const toMb = (bytes: number): number => bytes / MB
/** Bytes → GB, keeping `null` (unlimited) and `undefined` (not said) intact. */
function toGb<B extends number | null | undefined>(bytes: B): B {
  return (typeof bytes === 'number' ? bytes / GB : bytes) as B
}

/** An instant a person reads, or the word for "they never have". */
function whenLabel(instant: string | null | undefined): string {
  if (!instant) return t('time.never')
  return formatDateShort(instant.slice(0, 10))
}

/** "14:32" beside a date. The access log dates a row and then times it. */
function clockLabel(instant: string): string {
  const at = new Date(instant)
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`
}

/** Whole minutes between two instants — how long a visit lasted. */
function minutesBetween(from: string, to: string | null | undefined): number {
  if (!to) return 0
  return Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 60_000))
}

/**
 * Whole days since an instant, for "backed up N days ago".
 *
 * `null` means it has NEVER happened, which is a different sentence from a big
 * number — and `Infinity` printed into "backed up N days ago" is neither.
 */
function daysSince(instant: string | null | undefined): number | null {
  if (!instant) return null
  return Math.max(0, Math.floor((Date.now() - Date.parse(instant)) / 86_400_000))
}

/**
 * `TENANT_STATUSES` in the owner's own words, keyed by the code stored.
 *
 * Only consulted when the field that moved IS `status`: `TRIAL` is also a plan
 * code and `ACTIVE` is also a subscription status, and a free-text `wasText`
 * that happens to read `ACTIVE` is not this book's lifecycle.
 */
const BOOK_STATUS = {
  PENDING_VERIFICATION: 'o.history.bookStatus.PENDING_VERIFICATION',
  TRIAL: 'o.history.bookStatus.TRIAL',
  ACTIVE: 'o.history.bookStatus.ACTIVE',
  PAST_DUE: 'o.history.bookStatus.PAST_DUE',
  READ_ONLY: 'o.history.bookStatus.READ_ONLY',
  SUSPENDED: 'o.history.bookStatus.SUSPENDED',
  CANCELLED: 'o.history.bookStatus.CANCELLED',
} as const

/**
 * A stored code rendered as words.
 *
 * The audit log carries before/after values verbatim out of `before_json` and
 * `after_json`, so a role change reads `PARTNER` and a platform visit reads
 * `READ_ONLY`. Codes are what is stored; a label is what is shown, and it comes
 * from `t()` — so this maps the closed sets it recognises and leaves free text
 * alone.
 */
function codeLabel(text: string | null | undefined, field?: string | null): string | undefined {
  if (!text) return undefined
  if (field === 'status' && text in BOOK_STATUS) {
    return t(BOOK_STATUS[text as keyof typeof BOOK_STATUS])
  }
  if (text === 'READ_ONLY' || text === 'EDIT') return t(`o.history.mode.${text}`)
  if (text === 'OWNER' || text === 'PARTNER' || text === 'STAFF') return t(`role.${text}`)
  return text
}

/**
 * A refusal that is a legitimate answer, not a failure.
 *
 * `GET /platform/plans` is `platform.billing`, i.e. PLATFORM_OWNER only, but
 * three screens call it purely to turn a plan CODE into the owner's own wording
 * for it. For a support account the right outcome is the code, not an error
 * page over the whole screen — the plan name is a label, not the answer.
 */
async function orEmptyOnForbidden<T>(fetcher: () => Promise<T[]>): Promise<T[]> {
  try {
    return await fetcher()
  } catch (error) {
    if (error instanceof ApiRequestError && error.code === 'FORBIDDEN') return []
    throw error
  }
}

/* ── platform ─────────────────────────────────────────────────────────────── */

function fromOverview(data: PlatformOverviewResponse): PlatformOverview {
  return {
    today: data.today,
    monthlyRevenuePaisa: data.monthlyRevenuePaisa,
    revenueDeltaPaisa: data.revenueDeltaPaisa,
    payingCount: data.payingCount,
    averagePerBusinessPaisa: data.averagePerBusinessPaisa,
    trialCount: data.trialCount,
    totalBusinesses: data.totalBusinesses,
    animalsTracked: data.animalsTracked,
    cattleUnderManagementPaisa: data.cattleUnderManagementPaisa,
    profitBookedPaisa: data.profitBookedPaisa,
    cutOfProfit: data.cutOfProfit,
    pastDueCount: data.pastDueCount,
    pastDueAmountPaisa: data.pastDueAmountPaisa,
    pastDueShare: data.pastDueShare,
    payingByMonth: data.payingByMonth,
    newThisMonth: data.newThisMonth,
    trialsConverted: data.trialsConverted,
    cancelled: data.cancelledThisMonth,
    storageUsedGb: toGb(data.storageUsedBytes),
    storageLimitGb: toGb(data.storageLimitBytes),
    backupsDone: data.backupsDone,
    backupsTotal: data.backupsTotal,
    videoQueue: data.videoQueue,
    videoFailed24h: data.videoFailed24h,
  }
}

export const useOverview = (): FixtureQuery<PlatformOverview> =>
  useApi(['platform', 'overview'], async () =>
    fromOverview(await get<PlatformOverviewResponse>('/platform/overview')),
  )

function fromBusiness(row: BusinessSummary): Business {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    // The contract lets a business have no city. An absent city is a blank
    // cell, not the string "null".
    city: row.city ?? '',
    ownerName: row.ownerName,
    plan: row.planCode,
    status: row.status,
    animals: row.animals,
    users: row.users,
    bookValuePaisa: row.bookValuePaisa,
    storageMb: toMb(row.storageBytes),
    paysPaisa: row.paysPaisa,
    since: row.since,
    trialEndsOn: row.trialEndsOn ?? undefined,
  }
}

/**
 * The contract's page ceiling (`cursorQuerySchema`). It is a ceiling, not a
 * clamp: asking for 200 is a 400, not 100 rows. Everything the consoles show
 * fits in one page at this scale; `meta.nextCursor` pages the rest when it
 * does not, and no screen here yet asks for a second page.
 */
const PAGE_MAX = 100
const BUSINESS_PAGE = PAGE_MAX

export const useBusinesses = (): FixtureQuery<Business[]> =>
  useApi(
    ['platform', 'businesses'],
    async () => {
      const page = await getList<BusinessSummary[]>('/platform/businesses', {
        limit: BUSINESS_PAGE,
      })
      return page.data.map(fromBusiness)
    },
    { empty: () => [] },
  )

/**
 * P4 takes a tenant ULID; the console routes by slug, because a slug is what a
 * support agent can be told over the phone. The list already carries both, so
 * the id is resolved from the cached list rather than by a second search.
 */
function useBusinessId(slug: string): string | undefined {
  const businesses = useBusinesses()
  return businesses.data?.find((business) => business.slug === slug)?.id
}

export function useBusinessDetail(slug: string): FixtureQuery<BusinessDetailResponse> {
  const id = useBusinessId(slug)
  return useApi(
    ['platform', 'businessDetail', slug, id],
    async () => get<BusinessDetailResponse>(`/platform/businesses/${id ?? ''}`),
    { enabled: id !== undefined },
  )
}

/**
 * `null`, not `undefined`, for "no such business" — TanStack treats a query that
 * resolves to `undefined` as a bug and throws. Every screen already tests it for
 * truthiness, so the distinction never reaches one.
 */
export const useBusiness = (slug: string): FixtureQuery<Business | null> => {
  const id = useBusinessId(slug)
  // The SAME query as `useBusinessDetail` — same key, so one fetch serves both —
  // reshaped through `select`. It used to be a second query keyed on the
  // business id, which meant a write that changed the business but not its id
  // (every suspend, every plan change) refetched the detail and then handed the
  // screen the old mapping anyway.
  return useApi(
    ['platform', 'businessDetail', slug, id],
    async () => get<BusinessDetailResponse>(`/platform/businesses/${id ?? ''}`),
    {
      enabled: id !== undefined,
      select: (detail): Business | null =>
        detail
          ? {
              ...fromBusiness(detail.business),
              health: {
                lastBackupAt: detail.health.lastBackupAt ?? null,
                failedBackups7d: detail.health.failedBackups7d,
                videoQueue: detail.health.videoQueue,
                failedMedia24h: detail.health.failedMedia24h,
                lastActivityAt: detail.health.lastActivityAt ?? null,
              },
              // P4 carries the real next charge date and the real masked
              // method. Both were being invented on the screen instead.
              subscription: detail.subscription
                ? {
                    nextChargeOn: detail.subscription.nextChargeOn ?? null,
                    amountPaisa: detail.subscription.amountPaisa,
                    paymentMethodMasked: detail.subscription.paymentMethodMasked ?? null,
                  }
                : undefined,
            }
          : null,
    },
  )
}

/**
 * **NOT WIRED — there is no route.**
 *
 * `invoiceSchema` and `invoiceListResponse` exist in the contract, and P11/P12
 * act on `/platform/invoices/:id`, but nothing in `registry.ts` or in the API
 * SERVES an invoice list for a business. This is written as it will be, and it
 * currently fails with the API's own `NOT_FOUND` — which is the honest answer.
 * Returning `[]` would tell the reader the business has never been invoiced.
 */
/**
 * The contract's invoice → the row P-03 draws.
 *
 * There was no mapper here at all: the payload was cast straight to `Invoice`,
 * and `Invoice` asks for `date` and `forPeriod`, which no invoice has ever
 * carried — the contract says `issuedOn`, `periodStart` and `periodEnd`. So
 * `row.date` was `undefined` on every invoice ever rendered, and the
 * `formatDateShort(undefined)` inside the date column threw on `.split`,
 * taking the WHOLE of the business-detail screen down behind the error
 * boundary for any business that had ever been billed.
 *
 * It looked fine because the demo seed issues no invoices. A real book gets one
 * at the end of its first period.
 */
function fromInvoice(invoice: ContractInvoice): Invoice {
  return {
    id: invoice.id,
    // What the table's "Date" column means is the day the bill was raised.
    date: invoice.issuedOn,
    // "August" — the period billed, not the day it was billed on.
    forPeriod: monthName(invoice.periodStart),
    method: invoice.method ?? null,
    // Left `undefined` when the server omitted it: an actor without
    // `platform.billing` receives no `amountPaisa` key, and `<Money>` renders
    // that absence rather than printing a zero nobody stated.
    amountPaisa: invoice.amountPaisa,
    status: invoice.status,
  }
}

export const useInvoices = (slug: string): FixtureQuery<Invoice[]> => {
  const id = useBusinessId(slug)
  return useApi(
    ['platform', 'invoices', slug, id],
    async () =>
      (await getList<ContractInvoice[]>(`/platform/businesses/${id ?? ''}/invoices`)).data,
    { empty: () => [], enabled: id !== undefined, select: (rows) => rows.map(fromInvoice) },
  )
}

function fromPlan(plan: ContractPlan): Plan {
  return {
    id: plan.id,
    code: plan.code,
    name: plan.name,
    pricePaisa: plan.pricePaisa,
    // `null` from the API means "this plan is not a trial". Passing the null
    // through would read as `!== undefined` and price every plan at zero.
    trialDays: plan.trialDays ?? undefined,
    businesses: plan.businesses ?? 0,
    revenuePaisa: plan.revenuePaisa,
    features: plan.features.map((code) => t(`p.planFeature.${code}`)),
    featureCodes: plan.features,
    animalLimit: plan.maxAnimals,
    peopleLimit: plan.maxUsers,
    storageGb: toGb(plan.maxStorageBytes),
    isPublic: plan.isPublic,
    sortOrder: plan.sortOrder,
  }
}

/**
 * Cheapest first — the ladder a trader climbs, and the order every screen
 * reading this hook wants.
 *
 * The API orders by `sortOrder` then `code`, and `sortOrder` is 0 on every
 * plan because nothing in the console sets it. So the list arrives
 * alphabetical by code — FARM, TRADER, BASIC — which beside three prices
 * reads as no order at all.
 *
 * A price may be ABSENT rather than zero: money is left out of the payload
 * for an account that may not see it (`canSeeMoney`), and a plan with no
 * price is not a free plan. Those fall back to name, so the list is still
 * stable for a support account.
 */
function cheapestFirst(a: Plan, b: Plan): number {
  if (a.pricePaisa === undefined || b.pricePaisa === undefined) {
    return a.name.localeCompare(b.name)
  }
  return a.pricePaisa - b.pricePaisa || a.name.localeCompare(b.name)
}

export const usePlans = (): FixtureQuery<Plan[]> =>
  useApi(
    ['platform', 'plans'],
    async () => {
      const plans = await orEmptyOnForbidden(() => get<ContractPlan[]>('/platform/plans'))
      return plans.map(fromPlan).sort(cheapestFirst)
    },
    { empty: () => [] },
  )

/**
 * The payment-mix tile — P9 · `GET /platform/payment-mix`.
 *
 * It used to be scraped out of `platformSettingsResponse.gateways`, which
 * answers a different question: how many businesses have REGISTERED each
 * method. That is the rail a trader said he would use, not the one his money
 * came in on, and it carries no amount at all. It was also the wrong door —
 * `/platform/settings` is `PLATFORM_OWNER` only, so for a support account the
 * tile did not degrade, it failed.
 *
 * `amountPaisa` stays optional the whole way through. The server deletes the
 * key for an actor without `platform.billing`, and `<Money>` renders that
 * absence as `••••••`; substituting `0` here would be this file inventing a
 * claim about the platform's revenue.
 */
export const usePaymentMix = (): FixtureQuery<PaymentMix[]> =>
  useApi(
    ['platform', 'paymentMix'],
    async () => {
      const rows = await get<PaymentMixResponse>('/platform/payment-mix')
      return rows.map((row) => ({
        method: row.method,
        businesses: row.businesses,
        amountPaisa: row.amountPaisa,
      }))
    },
    { empty: () => [] },
  )

function fromSubscriptions(data: SubscriptionsSummaryResponse): SubscriptionsSummary {
  return {
    collectedPaisa: data.collectedPaisa,
    paidCount: data.paidCount,
    payingCount: data.payingCount,
    overduePaisa: data.overduePaisa,
    overdueCount: data.overdueCount,
    overdueShare: data.overdueShare,
    renewingPaisa: data.renewingPaisa,
    renewingCount: data.renewingCount,
    trialsEnding: data.trialsEnding,
    overdue: data.overdue.map((row) => ({
      businessId: row.businessId,
      slug: row.slug,
      name: row.name,
      city: row.city ?? '',
      animals: row.animals,
      plan: row.planCode,
      invoiceId: row.invoiceId,
      amountPaisa: row.amountPaisa,
      lateDays: row.lateDays,
      lastTried: row.lastTriedAt ? whenLabel(row.lastTriedAt) : t('common.none'),
      action: row.action,
    })),
    renewals: data.renewals.map((renewal) => ({
      date: renewal.date,
      name: renewal.name,
      amountPaisa: renewal.amountPaisa,
    })),
    moreRenewals: data.moreRenewals,
    failureReasons: data.failureReasons,
  }
}

export const useSubscriptions = (): FixtureQuery<SubscriptionsSummary> =>
  useApi(
    ['platform', 'subscriptions'],
    async () =>
      fromSubscriptions(await get<SubscriptionsSummaryResponse>('/platform/subscriptions')),
    {
      empty: () => ({
        collectedPaisa: 0,
        paidCount: 0,
        payingCount: 0,
        overduePaisa: 0,
        overdueCount: 0,
        renewingPaisa: 0,
        renewingCount: 0,
        trialsEnding: 0,
        overdue: [],
        renewals: [],
        moreRenewals: { count: 0, amountPaisa: 0 },
        failureReasons: [],
      }),
    },
  )

function fromUsage(data: UsageSummaryResponse): UsageSummary {
  return {
    storageUsedGb: toGb(data.storageUsedBytes),
    storageLimitGb: toGb(data.storageLimitBytes),
    growthGbPerMonth: toGb(data.growthBytesPerMonth),
    hostingCostPaisa: data.hostingCostPaisa,
    hostingShare: data.hostingShare,
    nearLimitCount: data.nearLimitCount,
    rows: data.rows.map((row) => ({
      tenantId: row.tenantId,
      slug: row.slug,
      name: row.name,
      plan: row.planCode,
      animals: row.animals,
      animalLimit: row.animalLimit,
      people: row.users,
      peopleLimit: row.userLimit,
      storageMb: toMb(row.storageBytes),
      storageLimitGb: toGb(row.storageLimitBytes),
      suggest: row.suggest
        ? { plan: row.suggest.planCode, upliftPaisa: row.suggest.upliftPaisa }
        : null,
    })),
    breakdown: data.breakdown.map((slice) => ({ label: slice.label, gb: toGb(slice.bytes) })),
    lever: {
      originalsGb: toGb(data.lever.originalsBytes),
      savedGb: toGb(data.lever.savedBytes),
      billAfterPaisa: data.lever.billAfterPaisa,
    },
  }
}

export const useUsage = (): FixtureQuery<UsageSummary> =>
  useApi(['platform', 'usage'], async () => {
    const page = await getList<UsageSummaryResponse>('/platform/usage', { limit: BUSINESS_PAGE })
    return fromUsage(page.data)
  })

function fromHealth(data: HealthSummaryResponse): HealthSummary {
  return {
    backupsDone: data.backupsDone,
    backupsTotal: data.backupsTotal,
    retries: data.retries,
    videoQueue: data.videoQueue,
    longestWaitMinutes: data.longestWaitMinutes,
    failed24h: data.failed24h,
    storageUsedGb: toGb(data.storageUsedBytes),
    storageLimitGb: toGb(data.storageLimitBytes),
    items: data.items.map((item) => ({
      id: item.id,
      what: item.what,
      detail: item.detail ?? undefined,
      business: item.tenantName ?? t('common.none'),
      // Carried through for P14's notify route, which addresses the tenant in
      // its path. Dropping it here is what left P-08 with a name it could not
      // turn back into a recipient.
      businessId: item.tenantId ?? undefined,
      when: whenLabel(item.at),
      action: item.action,
    })),
    biggestStores: data.biggestStores.map((store) => ({
      name: store.name,
      gb: toGb(store.bytes),
    })),
  }
}

export const useHealth = (): FixtureQuery<HealthSummary> =>
  useApi(['platform', 'health'], async () =>
    fromHealth(await get<HealthSummaryResponse>('/platform/health')),
  )

function fromTicket(ticket: ContractTicket): Ticket {
  return {
    id: ticket.id,
    number: ticket.number,
    person: ticket.raisedByName,
    slug: ticket.tenantSlug ?? '',
    business: ticket.tenantName ?? t('common.none'),
    onTrial: ticket.onTrial,
    subject: ticket.subject,
    channel: ticket.source,
    waiting: t('time.minutesAgo', { count: ticket.waitingMinutes }),
    // All three of TICKET_STATUSES, passed through. Collapsing CLOSED into
    // ANSWERED here was the other half of the bug the type re-export fixed: the
    // "Answered" pill counted closed tickets and read high.
    status: ticket.status,
    quote: ticket.quote ?? undefined,
    breakdown: ticket.breakdown,
  }
}

export const useTickets = (): FixtureQuery<Ticket[]> =>
  useApi(
    ['platform', 'tickets'],
    async () => {
      const page = await getList<ContractTicket[]>('/platform/support/tickets', { limit: PAGE_MAX })
      return page.data.map(fromTicket)
    },
    { empty: () => [] },
  )

/**
 * "What people ask about" — P15 · `GET /platform/support/asked-about`.
 *
 * This used to count categories in the one page of tickets the screen had
 * already loaded. That is arithmetic over a CURSOR-PAGED list: a sample
 * presented as a total, correct only while every ticket in the estate fits in
 * one page, and silently under-reporting from the next one on. The route
 * groups the whole table, already ranked, so there is nothing left to derive.
 */
export const useAskedAbout = (): FixtureQuery<{ label: string; count: number }[]> =>
  useApi(
    ['platform', 'askedAbout'],
    async () => get<AskedAboutResponse>('/platform/support/asked-about'),
    { empty: () => [] },
  )

function fromAccessLog(data: AccessLogResponse): AccessLogSummary {
  return {
    booksOpened: data.booksOpened,
    editedCount: data.editedCount,
    averageMinutes: Math.round(data.averageMinutes),
    ownersNotified: data.ownersNotified,
    entries: data.items.map((entry) => ({
      id: entry.id,
      at: entry.openedAt.slice(0, 10),
      time: clockLabel(entry.openedAt),
      person: entry.actorName,
      role: entry.actorRole,
      business: entry.tenantName,
      slug: entry.tenantSlug,
      reason: entry.reason,
      ticket: entry.ticketId ?? null,
      // `editsCount` is structurally 0 today: `access_log` is immutable by
      // design and nothing can stamp a row after it is written. `mode` is what
      // actually survives, so "edited" reads the mode. See the report.
      edited: entry.editsCount > 0 || entry.mode === 'EDIT',
      minutes:
        entry.durationSeconds != null
          ? Math.round(entry.durationSeconds / 60)
          : minutesBetween(entry.openedAt, entry.closedAt),
      mode: entry.mode,
    })),
  }
}

export const useAccessLog = (): FixtureQuery<AccessLogSummary> =>
  useApi(
    ['platform', 'accessLog'],
    async () => {
      const page = await getList<AccessLogResponse>('/platform/access-log', { limit: PAGE_MAX })
      return fromAccessLog(page.data)
    },
    {
      empty: () => ({
        booksOpened: 0,
        editedCount: 0,
        averageMinutes: 0,
        ownersNotified: 0,
        entries: [],
      }),
    },
  )

function fromTeamMember(member: ContractTeamMember): TeamMember {
  return {
    id: member.id,
    name: member.name,
    role: member.role,
    booksOpened: member.booksOpened,
  }
}

/**
 * P-11 is one screen assembled from three routes, because that is how the API
 * divides them: the settings row, the team table (PLATFORM_OWNER only, in both
 * directions — see `ops.routes.ts`) and the tenant count the broadcast copy
 * quotes. Support reads the screen and gets an empty team rather than a 403
 * across the whole page.
 */
export const useConsoleSettings = (): FixtureQuery<ConsoleSettings> =>
  useApi(['platform', 'consoleSettings'], async () => {
    const [settings, team, overview] = await Promise.all([
      get<PlatformSettingsResponse>('/platform/settings'),
      orEmptyOnForbidden(() => get<ContractTeamMember[]>('/platform/team')),
      get<PlatformOverviewResponse>('/platform/overview'),
    ])

    return {
      team: team.map(fromTeamMember),
      trialDays: settings.trialDays,
      askForMethod: settings.requirePaymentMethod,
      keepDataAfterTrial: settings.postTrialRetentionDays > 0,
      keepDataDays: settings.postTrialRetentionDays,
      gateways: settings.gateways,
      retryAfterDays: settings.dunningRetryAfterDays,
      largestVideoMb: Math.round(toMb(settings.maxVideoBytes)),
      keepOriginals: settings.keepOriginalVideos ? 'FOREVER' : 'DROPPED',
      // `keepOriginalVideoDays`, not `postTrialRetentionDays`: the screen was
      // labelling the video policy with how long a CANCELLED book's data is kept.
      keepOriginalDays: settings.keepOriginalVideoDays ?? null,
      backupAt: `${String(settings.nightlyBackupHourPkt).padStart(2, '0')}:00`,
      maintenance: settings.maintenanceMode,
      // One owner per book: the broadcast reaches every business, once.
      ownerCount: overview.totalBusinesses,
    }
  })

/* ── a trader's book, seen from either console ────────────────────────────── */

function fromAnimal(animal: AnimalSummary): BookAnimal {
  return {
    id: animal.id,
    // A tag and a nickname are both optional in the contract — plenty of animals
    // arrive with neither, and the deck's tag chip has to say something.
    tag: animal.tagNumber ?? '—',
    nickname: animal.nickname ?? animal.tagNumber ?? t('common.none'),
    // Both are nullable in the contract: an animal bought in a hurry may have
    // neither recorded, and an em-dash is the truthful cell for that.
    breed: animal.breed ? t(`breed.${animal.breed}`) : t('common.none'),
    teeth: teethLabel(animal.teethAtPurchase as TeethAgeCode | null | undefined),
    daysHeld: animal.daysHeld,
    paidPaisa: animal.purchasePricePaisa,
    costNowPaisa: animal.costBasisPaisa,
    breakEvenPaisa: animal.breakEvenPaisa,
    askingPaisa: animal.askingPricePaisa,
    soldPaisa: animal.salePricePaisa,
    status: animal.status,
    coverMediaId: animal.coverMediaId ?? undefined,
  }
}

/**
 * P-04's whole payload, and the ONE hook that needs a P5 grant.
 *
 * Without a live grant this resolves to `undefined` — which is not a failure,
 * it is the rule: the platform console may not read a trader's animals, prices
 * or profit until a book has been opened with a reason, and the row saying so
 * has been written. Every request below carries the grant's own scoped token
 * (`grant.token`), because the console's cookie session is deliberately not
 * bound to the grant.
 *
 * Cost, break-even and profit are READ. Nothing here recomputes one; the only
 * arithmetic is the minute counter on the banner.
 */
async function loadBook(business: Business, grant: AccessGrant): Promise<BookSummary> {
  // The calendar month the visit falls in, as a half-open range collapsed to
  // its inclusive last day. Never "-31": four months do not have one, and the
  // API validates the date rather than clamping it.
  const [year = 0, month = 1] = grant.openedAt.slice(0, 10).split('-').map(Number)
  const from = `${year}-${String(month).padStart(2, '0')}-01`
  const nextMonthFirst =
    month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`
  const to = addDays(nextMonthFirst, -1)

  const [dashboard, animals, breakdown, reality] = await Promise.all([
    get<DashboardResponse>('/dashboard', undefined, grant.token),
    // 100 is the contract's real ceiling, not a clamp — over-large is a 400.
    getList<AnimalSummary[]>('/animals', { limit: PAGE_MAX }, grant.token),
    get<CostBreakdownReportResponse>('/reports/cost-breakdown', { from, to }, grant.token),
    get<RealityCheckResponse>('/expenses/reality-check', undefined, grant.token),
  ])

  return {
    slug: business.slug,
    name: business.name,
    city: business.city,
    ownerName: business.ownerName,
    inStock: dashboard.inStock,
    sold: dashboard.soldThisSeason,
    moneyTiedUpPaisa: dashboard.moneyTiedUpPaisa,
    dailyCostPaisa: dashboard.dailyBurnPaisa,
    profitBookedPaisa: dashboard.profitBookedPaisa,
    profitBookedDaysHeld: dashboard.profitBookedDaysHeld,
    owedPaisa: dashboard.receivableTotalPaisa,
    animals: animals.data.map(fromAnimal),
    monthlyCosts: breakdown.bars.map((bar) => ({
      label: bar.label,
      amountPaisa: bar.amountPaisa,
    })),
    // The pair the note is about: what the book ASSUMES a day, against what the
    // real bills say it is. Both come from the engine; the screen subtracts.
    perAnimalPerDayPaisa: reality.actualPerDayPaisa,
    feedEstimatePaisa: reality.assumedPerDayPaisa,
  }
}

/**
 * One animal of an opened book, in full — `GET /animals/:id` through the grant's
 * token, exactly as the trader's own phone reads it. Disabled without a grant.
 */
export const useBookAnimal = (
  slug: string,
  animalId: string | null,
): FixtureQuery<AnimalDetail> => {
  const grant = useAccessGrantFor(slug)
  return useApi(
    ['bookAnimal', slug, animalId, grant?.accessId],
    async () => get<AnimalDetail>(`/animals/${animalId ?? ''}`, undefined, grant?.token),
    { enabled: animalId !== null && grant !== null },
  )
}

export const useBook = (slug: string): FixtureQuery<BookSummary | null> => {
  const business = useBusiness(slug)
  const grant = useAccessGrantFor(slug)

  return useApi(
    ['book', slug, grant?.accessId],
    async () => {
      if (!business.data || !grant) return null
      return loadBook(business.data, grant)
    },
    { enabled: !business.isPending },
  )
}

/* ── owner console ────────────────────────────────────────────────────────── */

function fromPerson(person: ContractPerson): Person {
  return {
    id: person.id,
    name: person.name,
    note: person.note ?? undefined,
    username: person.username,
    role: person.role,
    lastSignedIn: whenLabel(person.lastSignedInAt),
    isYou: person.isYou,
  }
}

/**
 * What CATTLE BOOK did to this book, in the trader's own words (ARCH §8.6).
 *
 * Every platform act writes a row into the trader's log deliberately, so that
 * he sees what was done to his book from his side. He can only see it if it
 * says something: before this, everything except a book visit fell through to
 * the generic template and a `CREATE` on `tenant` read "Added this business",
 * which is not merely unhelpful — it is false.
 *
 * The wording is derived from what the payload STATES, never from a guess at
 * which platform screen was pressed. `auditEntrySchema` carries neither the
 * dotted action code (`platform.owner_notified`) nor the row's `summary` nor
 * `after_json`; what survives is the verb, the entity and the ONE field the
 * server chose to show as Was → Now. So a status landing on `SUSPENDED` is
 * reported as a suspension because that is what his book's status now is, and
 * a row nothing here recognises names the field rather than inventing a reason
 * for it.
 */
function platformSentence(entry: AuditEntry): string {
  const field = entry.field

  if (entry.entity === 'invoice') {
    // P12 writes only the channel it sent on; P11 bumps the attempt count.
    if (field === 'channel') return t('o.history.platform.invoiceReminded')
    if (field === 'retryCount') return t('o.history.platform.invoiceRetried')
  }

  if (entry.entity === 'tenant') {
    // P14 mints a `notificationId` against the book, and nothing else does.
    if (field === 'notificationId') return t('o.history.platform.notified')
    // The trial's end moved. It is only ever moved outwards (`days` is >= 1).
    if (field === 'trialEndsAt') return t('o.history.platform.trialExtended')
    if (field === 'status') {
      // Where his book ENDED UP, which is the fact he needs and the one the
      // payload actually carries. A trial that lapsed on its own and a book put
      // into read-only by hand are the same sentence to him, and both are true.
      if (entry.nowText === 'READ_ONLY') return t('o.history.platform.readOnly')
      if (entry.nowText === 'SUSPENDED') return t('o.history.platform.suspended')
      if (entry.nowText === 'CANCELLED') return t('o.history.platform.closed')
      if (entry.nowText === 'TRIAL') return t('o.history.platform.trialExtended')
    }
    if (entry.action === 'CREATE' && field === 'name') return t('o.history.platform.created')
  }

  return field
    ? t('o.history.platform.otherField', { field })
    : t('o.history.platform.other')
}

/**
 * "Asif edited an animal" is useless on its own, so the row carries both sides.
 * The API says WHICH field changed and what it was; the sentence is composed
 * here because a message template is a string and belongs in `t()`.
 */
function fromAuditEntry(entry: AuditEntry): ActivityEntry {
  const action = t(`o.history.action.${entry.action}`)
  const entity = t(`o.history.entity.${entry.entity}` as never) || String(entry.entity)

  /*
    A platform visit is not "someone added a tenant", which is what the generic
    template makes of `CREATE` on `tenant`. The row carries `platformAccessId`,
    so the console KNOWS what it is looking at and can say it — this is the one
    screen a trader opens to find out whether support has been in his book.

    `platformAccessId` marks the OPENING of a book — written against `tenant`,
    the book itself — but it is also stamped on every edit made from inside
    during that visit. Those are real changes to an animal or a price and read
    as such; the Who column already names the support engineer who made them.

    Everything else a platform actor does reaches this book from OUTSIDE it (no
    grant, so no `platformAccessId`): suspending it, ending or extending its
    trial, chasing a bill, sending its owner a message. `platformSentence` says
    each of those in his words.
  */
  const isPlatform = entry.actorKind === 'PLATFORM'
  const isPlatformVisit = isPlatform && entry.platformAccessId != null && entry.entity === 'tenant'
  const opaqueId = isPlatform && (entry.field === 'accessId' || entry.field === 'notificationId')
  const generic = entry.entityLabel
    ? t('o.history.entryWithLabel', { action, entity, label: entry.entityLabel })
    : entry.field
      ? t('o.history.entryField', { action, entity, field: entry.field })
      : t('o.history.entry', { action, entity })

  const what = isPlatformVisit
    ? t('o.history.platformVisit')
    : isPlatform && entry.platformAccessId == null
      ? platformSentence(entry)
      : generic

  return {
    id: entry.id,
    at: `${formatDateShort(entry.at.slice(0, 10))} ${clockLabel(entry.at)}`,
    person: entry.actorName,
    // A platform actor inside the book has no role IN the book. The console
    // still has to name a role, and OWNER is what the grant behaves as.
    role: entry.actorRole ?? 'OWNER',
    what,
    wasPaisa: entry.wasPaisa,
    nowPaisa: entry.nowPaisa,
    // A ULID is not a value a person can read. `accessId` and `notificationId`
    // are the fields the server picks as Was → Now for a book visit and for a
    // message to the owner, and both arrive as raw ids; the sentence already
    // says what happened, so the columns say nothing rather than say a ULID.
    // Nothing is suppressed here that a person could have read — and no money
    // key is touched: `wasPaisa`/`nowPaisa` pass through untouched, gated on
    // the screen.
    wasText: opaqueId ? undefined : codeLabel(entry.wasText, entry.field),
    nowText: opaqueId ? undefined : codeLabel(entry.nowText, entry.field),
    kind: entry.kind,
    restorable: entry.restorable,
  }
}

/**
 * O-01 and O-04 read from this one hook, which is why it carries the whole
 * audit log rather than the four rows the overview shows. Five routes, one
 * payload: the deck's first screen must not cost five round trips on a mandi
 * connection, and TanStack de-duplicates the shared ones anyway.
 */
export const useOwnerOverview = (): FixtureQuery<OwnerOverview> => {
  const { me } = useSessionState()

  return useApi(
    ['owner', 'overview', me?.tenant.id],
    async () => {
      const [dashboard, audit, people, backups] = await Promise.all([
        get<DashboardResponse>('/dashboard'),
        getList<AuditEntry[]>('/audit-log', { limit: PAGE_MAX }),
        get<ContractPerson[]>('/people'),
        get<BackupListResponse>('/backups'),
      ])

      return {
        today: dashboard.asOf,
        ownerName: me?.user.name ?? '',
        businessName: me?.tenant.name ?? '',
        lastBackupDaysAgo: daysSince(backups.lastFullAt),
        inStock: dashboard.inStock,
        soldThisSeason: dashboard.soldThisSeason,
        moneyTiedUpPaisa: dashboard.moneyTiedUpPaisa,
        dailyCostPaisa: dashboard.dailyBurnPaisa,
        profitBookedPaisa: dashboard.profitBookedPaisa,
        profitBookedDaysHeld: dashboard.profitBookedDaysHeld,
        activity: audit.data.map(fromAuditEntry),
        people: people.map(fromPerson),
      }
    },
    { enabled: me !== null },
  )
}

/**
 * People, from whichever side is asking.
 *
 * In the Owner Console this is `GET /people`. In the Platform Console the same
 * panel appears on P-03, and the platform actor has NO right to that route —
 * it is `people.manage`, and a read-only visit deliberately does not carry it.
 * The people on P-03 therefore come out of the business detail payload, which
 * is the platform's own view of a book. The screen never learns the difference,
 * which is the entire point of this file.
 */
export const usePeople = (): FixtureQuery<Person[]> => {
  const { surface, me } = useSessionState()
  const { slug = '' } = useParams<{ slug: string }>()
  const detail = useBusinessDetail(surface === 'platform' ? slug : '')

  const fromDetail = surface === 'platform'

  return useApi(
    ['people', surface, fromDetail ? slug : me?.tenant.id],
    async () => {
      if (fromDetail) {
        return (detail.data?.people ?? []).map((person) => ({
          id: person.id,
          name: person.name,
          username: person.username,
          role: person.role,
          lastSignedIn: whenLabel(person.lastSignedInAt),
          isYou: false,
        }))
      }
      return (await get<ContractPerson[]>('/people')).map(fromPerson)
    },
    {
      empty: () => [],
      enabled: fromDetail ? detail.data !== undefined : me !== null,
    },
  )
}

export const useBackup = (): FixtureQuery<BackupState> =>
  useApi(['owner', 'backup'], async () => {
    const [backups, storage] = await Promise.all([
      get<BackupListResponse>('/backups'),
      get<StorageUsageResponse>('/storage-usage'),
    ])

    const lastFull = backups.items.find((backup) => backup.kind === 'FULL' && backup.status === 'READY')
    const bookOnly = backups.items.find(
      (backup) => backup.kind === 'BOOK_ONLY' && backup.status === 'READY',
    )

    return {
      lastFullDaysAgo: daysSince(backups.lastFullAt),
      lastFullDate: (backups.lastFullAt ?? '').slice(0, 10),
      fullSizeMb: toMb(lastFull?.byteSize ?? storage.usedBytes),
      bookOnlyMb: toMb(bookOnly?.byteSize ?? 0),
      breakdown: storage.breakdown.map((slice) => ({
        label: slice.label,
        mb: toMb(slice.bytes),
      })),
      automaticRunning: backups.automaticEnabled,
      nightlyHourPkt: backups.nightlyHourPkt ?? null,
      // Codes, not labels — the screen tones the badge off the code and looks
      // the word up itself. `null` is "no nightly copy has run yet".
      lastNightlyStatus: backups.lastNightlyStatus ?? t('common.none'),
      copiesKept: t('o.backup.copiesKeptValue', { count: backups.copiesKept }),
      recordsCheck: lastFull?.rowCounts ? 'READY' : t('common.none'),
    }
  })

function fromSettings(settings: TenantSettingsResponse): OwnerSettings {
  return {
    businessName: settings.businessName,
    grouping: settings.currencyGrouping,
    priceLockOn: settings.priceLockEnabled,
    autoLockSeconds: settings.autoLockSeconds,
    pinSet: settings.pinSet,
    // The date part only: <RelativeTime> compares whole days in Asia/Karachi.
    pinChangedAt: settings.pinChangedAt ? settings.pinChangedAt.slice(0, 10) : null,
    buyCommissionBps: settings.defaultBuyCommissionBps,
    sellCommissionBps: settings.defaultSellCommissionBps,
    otherSaleCostsPaisa: settings.expectedSaleCostsPaisa,
    feedEstimatePaisa: settings.defaultDailyRatePaisa,
    actualPerAnimalPerDayPaisa: settings.actualPerAnimalPerDayPaisa,
  }
}

export const useOwnerSettings = (): FixtureQuery<OwnerSettings> => {
  const { me } = useSessionState()
  return useApi(
    ['owner', 'settings', me?.tenant.id],
    async () => fromSettings(await get<TenantSettingsResponse>('/settings')),
    { enabled: me !== null },
  )
}

/** Exposed so a screen can refetch the exact query a mutation invalidated. */
export const queryKeys = {
  platformOverview: ['platform', 'overview'] as const,
  businesses: ['platform', 'businesses'] as const,
  businessDetail: (slug: string) => ['platform', 'businessDetail', slug] as const,
  plans: ['platform', 'plans'] as const,
  consoleSettings: ['platform', 'consoleSettings'] as const,
  tickets: ['platform', 'tickets'] as const,
  accessLog: ['platform', 'accessLog'] as const,
  subscriptions: ['platform', 'subscriptions'] as const,
  usage: ['platform', 'usage'] as const,
  health: ['platform', 'health'] as const,
  paymentMix: ['platform', 'paymentMix'] as const,
  askedAbout: ['platform', 'askedAbout'] as const,
  invoices: (slug: string) => ['platform', 'invoices', slug] as const,
  /**
   * Kept as an alias of `businessDetail` rather than deleted: `useBusiness` now
   * derives from the detail query through `select`, so there is no separate
   * cache entry to invalidate. Pointing it at the real key means a caller that
   * invalidates both still refetches, instead of silently invalidating nothing.
   */
  book: (slug: string) => ['platform', 'businessDetail', slug] as const,
  ownerOverview: ['owner', 'overview'] as const,
  ownerSettings: ['owner', 'settings'] as const,
  people: ['people'] as const,
  backup: ['owner', 'backup'] as const,
}

export type { MeResponse }
